import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Workbook, type Worksheet } from 'exceljs';
import * as fs from 'file-saver';

@Injectable({
  providedIn: 'root',
})
export class DownloadXlsxService {
  megaDetailsList: any;
  collateralDetailsList: any;
  issuer = '';

  private datePipe = inject(DatePipe);

  parseCollateralDetails(collateralDetailsList: any[]): unknown[][] {
    const collateralData: unknown[][] = [];
    for (const collat of collateralDetailsList) {
      const collatSubData: unknown[] = [];
      if (collat.issuer.match('FN')) {
        this.issuer = 'Fannie Mae';
      } else if (collat.issuer.match('FR')) {
        this.issuer = 'Freddie Mac';
      }
      collatSubData.push(collat.prefix);
      collatSubData.push(collat.collateralPoolNumber);
      collatSubData.push(collat.cusip);
      collatSubData.push(collat.coupon);
      collatSubData.push(this.issuer);
      collatSubData.push(collat.issueDate);
      collatSubData.push(this.datePipe.transform(collat.maturityDate, 'MM-yyyy'));
      collatSubData.push(collat.upbAmount);
      collatSubData.push(collat.upbAmountWithFactor);
      collatSubData.push(collat.mbsWamTerm);
      collatSubData.push(collat.mbsWacRate);

      collateralData.push(collatSubData);
    }
    return collateralData;
  }

  parseMegaDetails(megaDetailsList: any): unknown[][] {
    const megaDataList: unknown[][] = [];
    const megaDetailsData: unknown[] = [];

    megaDetailsData.push(megaDetailsList.megaPrefix);
    megaDetailsData.push(megaDetailsList.megaIssueDate);
    megaDetailsData.push(this.datePipe.transform(megaDetailsList.maturityDate, 'MM-yyyy'));
    megaDetailsData.push(megaDetailsList.coupon);
    megaDetailsData.push(megaDetailsList.dealAmount);
    megaDetailsData.push(megaDetailsList.fannieIssued / 100);
    megaDetailsData.push(megaDetailsList.freddieIssued / 100);
    megaDetailsData.push(megaDetailsList.monthsToMaturity);

    if (megaDetailsList.subType != null) {
      megaDetailsData.push(megaDetailsList.subType);
    }
    if (megaDetailsList.mfHighCoupon != null) {
      megaDetailsData.push(megaDetailsList.mfHighCoupon);
    }
    if (megaDetailsList.mfLowCoupon != null) {
      megaDetailsData.push(megaDetailsList.mfLowCoupon);
    }

    megaDataList.push(megaDetailsData);

    return megaDataList;
  }

  setWorksheetCloumn(worksheet: Worksheet, megaType: string) {
    const title =
      'Fannie Mae Collateral Analyzer (File created: ' +
      this.datePipe.transform(new Date(), 'MM/dd/yyyy hh:mm a') +
      ')';
    const disclosureNote = 'Disclosure data herein is preliminary and subject to change';

    worksheet.getColumn(1).width = 10;
    worksheet.getColumn(2).width = 15;
    worksheet.getColumn(3).width = 15;
    worksheet.getColumn(4).width = 15;
    worksheet.getColumn(5).width = 25;
    worksheet.getColumn(6).width = 15;
    worksheet.getColumn(7).width = 15;
    worksheet.getColumn(8).width = 25;
    worksheet.getColumn(9).width = 25;
    worksheet.getColumn(10).width = 25;
    worksheet.getColumn(11).width = 20;

    const titleRow = worksheet.getRow(1);
    titleRow.font = { name: 'Arial', size: 12, bold: true };
    titleRow.values = [title];
    titleRow.height = 36;
    worksheet.mergeCells('A1:E1');

    const disclNote = worksheet.getRow(2);
    disclNote.values = [disclosureNote];
    worksheet.mergeCells('A2:E2');
    disclNote.font = {
      name: 'Arial',
      size: 12,
      bold: false,
      italic: true,
      color: { argb: 'b6b6b6' },
    };
    disclNote.height = 36;

    const megaHeading = worksheet.getRow(3);
    megaHeading.values = ['Mega/Supers Characteristics'];
    megaHeading.font = { name: 'Arial', size: 14, bold: true };
    megaHeading.height = 50;
    megaHeading.alignment = { vertical: 'center' };
    worksheet.mergeCells('A3:Z3');

    const header: string[] = [];

    header.push('Prefix');
    header.push('Issue Date');
    header.push('Maturity Date');
    header.push('WA Net Interest Rate');
    header.push('Pool Amount');
    header.push('FMNA Issued (%)');
    header.push('FHLMC Issued (%)');
    header.push('WA Months to Maturity');
    if (megaType === 'SFARMMEGA' || megaType === 'MFARMMEGA') {
      header.push('Subtype');
    }
    if (megaType === 'MFFIXMEGA') {
      header.push('High Coupon');
      header.push('Low Coupon');
    }

    const headerRow = worksheet.getRow(4);
    headerRow.values = header;
    headerRow.font = { name: 'Arial', size: 12, bold: false };
    headerRow.height = 50;
    headerRow.eachCell((cell, _number) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E6E6E6' },
      };
      cell.alignment = { wrapText: true, vertical: 'top' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    const CollateralDetails = worksheet.getRow(6);
    CollateralDetails.values = ['Collateral'];
    CollateralDetails.font = { name: 'Arial', size: 14, bold: true };
    CollateralDetails.height = 36;
    CollateralDetails.alignment = { vertical: 'center' };
    worksheet.mergeCells('A6:Z6');

    const subHeader = [
      'Prefix',
      'Collateral Pool #',
      'CUSIP',
      'WA Net Interest Rate',
      'Issuer',
      'Issue Date',
      'Maturity Date',
      'Collateral Issuance Contributing to UPB',
      'Collateral Current Contributing to UPB',
      'WA MONTHS TO MATURITY',
      'WA  Interest Rate at Settlement',
    ];

    const subHeaderRow = worksheet.getRow(7);
    subHeaderRow.values = subHeader;
    subHeaderRow.height = 52;
    subHeaderRow.font = { name: 'Arial', size: 12, bold: false };
    subHeaderRow.eachCell((cell, _number) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E6E6E6' },
      };
      cell.alignment = { wrapText: true, vertical: 'top' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
  }

  generateExcel(collateralValidatedPoolListRes: any) {
    let collateralData: unknown[][] = [];

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Collateral');
    this.setWorksheetCloumn(worksheet, collateralValidatedPoolListRes.megaType);

    this.parseMegaDetails(collateralValidatedPoolListRes.megaDetails).forEach((d) => {
      const row = worksheet.getRow(5);
      row.values = d;
      row.font = { name: 'Arial', size: 12, bold: false };
      row.height = 36;
    });
    worksheet.getCell('E5').numFmt = '$0,000.00';
    worksheet.getCell('F5').numFmt = '0.00%';
    worksheet.getCell('G5').numFmt = '0.00%';

    let counter = 8;

    collateralData = this.parseCollateralDetails(collateralValidatedPoolListRes.collateralDetails);

    collateralData.forEach((cd: unknown[]) => {
      const row = worksheet.getRow(counter);
      row.values = cd;
      row.font = { name: 'Arial', size: 12, bold: false };
      row.height = 36;
      worksheet.getCell('H' + counter).numFmt = '$0,000.00';
      worksheet.getCell('I' + counter).numFmt = '$0,000.00';
      counter++;
    });

    try {
      workbook.xlsx.writeBuffer().then((data) => {
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        fs.saveAs(
          blob,
          'FNMA_Prelim-Disclosure_' + this.datePipe.transform(new Date(), 'yyyyMMdd') + '.xlsx',
        );
      });
    } catch (err) {
      console.log(err);
    }
  }

  parseDealerDeatails(stContactDetails: any[]): unknown[][] {
    const list2: unknown[][] = [];
    stContactDetails.forEach((d: any) => {
      const list1: unknown[] = [];
      list1.push(d.orgName);
      list1.push(d.dealerFirstName);
      list1.push(d.dealerLastName);
      list1.push(d.userId);
      list1.push(d.phoneNumber);
      list1.push(d.email);
      list1.push(d.orgId);
      list1.push(d.contactStatus);
      list2.push(list1);
    });

    return list2;
  }

  parseOrgDetails(orgDetails: any[]): unknown[][] {
    const list2: unknown[][] = [];
    orgDetails.forEach((d: any) => {
      const list1: unknown[] = [];
      list1.push(d.orgName);
      list1.push(d.dealerCount);
      list2.push(list1);
    });

    return list2;
  }

  generateContactReportExcel(payload: any) {
    const workbook = new Workbook();
    const activeDealerSheet = workbook.addWorksheet('Active Dealers List');
    const orgSheet = workbook.addWorksheet('Org List');
    const dealerDetails: any = payload.stContactDetails;
    const orgDetails: any = payload.stOrgDetails;
    let counter = 2;

    activeDealerSheet.getColumn(1).width = 15;
    activeDealerSheet.getColumn(2).width = 15;
    activeDealerSheet.getColumn(3).width = 15;
    activeDealerSheet.getColumn(4).width = 10;
    activeDealerSheet.getColumn(5).width = 15;
    activeDealerSheet.getColumn(6).width = 25;
    activeDealerSheet.getColumn(7).width = 10;
    activeDealerSheet.getColumn(8).width = 5;
    orgSheet.getColumn(1).width = 30;
    orgSheet.getColumn(2).width = 10;

    const row = activeDealerSheet.getRow(1);
    const row1 = orgSheet.getRow(1);

    row.values = [
      'orgName',
      'dealerFirstName',
      'dealerLastName',
      'userId',
      'phoneNumber',
      'email',
      'orgId',
      'contactStatus',
    ];
    row.font = { name: 'Arial', size: 12, bold: false };
    row1.values = ['orgName', 'dealerCount'];
    row1.font = { name: 'Arial', size: 12, bold: false };

    this.parseDealerDeatails(dealerDetails).forEach((details) => {
      const row2 = activeDealerSheet.getRow(counter);
      row2.values = details;
      row2.font = { name: 'Arial', size: 12, bold: false };
      counter++;
    });
    counter = 2;
    this.parseOrgDetails(orgDetails).forEach((details) => {
      const row3 = orgSheet.getRow(counter);
      row3.values = details;
      row3.font = { name: 'Arial', size: 12, bold: false };
      counter++;
    });

    try {
      workbook.xlsx.writeBuffer().then((data) => {
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        fs.saveAs(
          blob,
          'st_org_dealer_details_' + this.datePipe.transform(new Date(), 'yyyyMMdd') + '.xlsx',
        );
      });
    } catch (err) {
      console.log(err);
    }
  }

  generateMegaExcel(res: any, fileName: string) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    let counter = 2;
    let row = worksheet.getRow(1);
    row.values = ['cusip', 'contribOrigFace'];
    row.font = { name: 'Arial', size: 12, bold: false };
    this.parseMegaListingDetails(res).forEach((details) => {
      row = worksheet.getRow(counter);
      row.values = details;
      counter++;
    });

    try {
      workbook.xlsx.writeBuffer().then((megaData: any) => {
        const blob = new Blob([megaData], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        fs.saveAs(blob, fileName + '.xlsx');
      });
    } catch (err) {
      console.log(err);
    }
  }

  generateRCRVolumeReportExcel(res: any, fileName: string) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    let counter = 2;
    let row = worksheet.getRow(1);
    row.values = ['Month', 'Deal Type', 'Count', 'Current Face Amount', 'Exchange Fee Dollars'];
    row.font = { name: 'Arial', size: 12, bold: false };
    this.parseRCRVolumeReportData(res).forEach((details) => {
      row = worksheet.getRow(counter);
      row.values = details;
      counter++;
    });

    try {
      workbook.xlsx.writeBuffer().then((res2: any) => {
        const blob = new Blob([res2], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        fs.saveAs(blob, fileName + '.xlsx');
      });
    } catch (err) {
      console.log(err);
    }
  }

  parseMegaListingDetails(res: any[]): unknown[][] {
    const listSettled: unknown[][] = [];
    res.forEach((d: any) => {
      const tempList: unknown[] = [];
      tempList.push(d.cusip);
      tempList.push(d.contribOrigFace);
      listSettled.push(tempList);
    });

    return listSettled;
  }

  parseRCRVolumeReportData(res: any[]): unknown[][] {
    const listVolumeData: unknown[][] = [];
    res.forEach((d: any) => {
      const tempList: unknown[] = [];
      tempList.push(d.Month);
      tempList.push(d['Deal Type']);
      tempList.push(d.Count);
      tempList.push(d['Current Face Amount']);
      tempList.push(d['Exchange Fee Dollars']);
      listVolumeData.push(tempList);
    });

    return listVolumeData;
  }
}
